import type {
  UploadResponse,
  ListFilesResponse,
  FileItem,
  LoginResponse,
  SpaceType,
  ListScope,
  FileCategory,
} from '@/types'

const API_BASE = import.meta.env.VITE_API_BASE || 'https://senyao-system.xiangyu97-wang.workers.dev'

const TOKEN_KEY = 'senyao_token'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}
export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token)
}
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY)
}

function authHeaders(): Record<string, string> {
  const token = getToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function parseJson<T>(resp: Response): Promise<T> {
  const text = await resp.text()
  try { return JSON.parse(text) as T } catch { return { success: false, error: text } as any }
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

/** 钉钉免登：用 authCode 换 JWT */
export async function dingtalkLogin(code: string): Promise<LoginResponse> {
  try {
    const resp = await fetch(`${API_BASE}/auth/dingtalk/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })
    return await parseJson<LoginResponse>(resp)
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Login failed' }
  }
}

/** 紧急账号登录（兜底） */
export async function emergencyLogin(username: string, password: string): Promise<LoginResponse> {
  try {
    const resp = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    })
    return await parseJson<LoginResponse>(resp)
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Login failed' }
  }
}

// ---------------------------------------------------------------------------
// Upload
// ---------------------------------------------------------------------------

export interface UploadOptions {
  space?: SpaceType
  deptId?: number
  sharedDeptIds?: number[]
  tags?: string[]
  onProgress?: (percent: number) => void
}

export function uploadFileXHR(file: File, options: UploadOptions = {}): Promise<UploadResponse> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest()
    const formData = new FormData()
    formData.append('file', file)
    if (options.space) formData.append('space', options.space)
    if (options.deptId != null) formData.append('dept_id', String(options.deptId))
    if (options.sharedDeptIds?.length) formData.append('shared_dept_ids', JSON.stringify(options.sharedDeptIds))
    if (options.tags?.length) formData.append('tags', JSON.stringify(options.tags))

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && options.onProgress) {
        options.onProgress(Math.round((e.loaded / e.total) * 100))
      }
    })
    xhr.addEventListener('load', () => {
      try { resolve(JSON.parse(xhr.responseText) as UploadResponse) }
      catch { resolve({ success: false, error: 'Invalid response' }) }
    })
    xhr.addEventListener('error', () => resolve({ success: false, error: 'Upload failed' }))
    xhr.addEventListener('abort', () => resolve({ success: false, error: 'Upload cancelled' }))
    xhr.open('POST', `${API_BASE}/upload`)
    for (const [k, v] of Object.entries(authHeaders())) xhr.setRequestHeader(k, v)
    xhr.send(formData)
  })
}

// ---------------------------------------------------------------------------
// Resources
// ---------------------------------------------------------------------------

export interface ListOptions {
  category?: FileCategory
  scope?: ListScope
  q?: string
  limit?: number
  offset?: number
}

export async function listFiles(options: ListOptions = {}): Promise<ListFilesResponse> {
  const params = new URLSearchParams()
  if (options.category && options.category !== 'all') {
    const map: Record<Exclude<FileCategory, 'all'>, string> = {
      images: 'image', documents: 'document', other: 'other',
    }
    params.set('category', map[options.category])
  }
  if (options.scope) params.set('scope', options.scope)
  if (options.q) params.set('q', options.q)
  if (options.limit != null) params.set('limit', String(options.limit))
  if (options.offset != null) params.set('offset', String(options.offset))

  try {
    const resp = await fetch(`${API_BASE}/resources?${params.toString()}`, {
      headers: authHeaders(),
    })
    const data = await parseJson<{ success: boolean; data?: FileItem[]; error?: string }>(resp)
    if (data.success && Array.isArray(data.data)) {
      return { success: true, data: data.data }
    }
    return { success: false, data: [], error: data.error }
  } catch (error) {
    return { success: false, data: [], error: error instanceof Error ? error.message : 'Failed' }
  }
}

export async function deleteFile(key: string): Promise<{ success: boolean; error?: string }> {
  try {
    const resp = await fetch(`${API_BASE}/resources/${encodeURIComponent(key)}`, {
      method: 'DELETE',
      headers: authHeaders(),
    })
    return await parseJson(resp)
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Delete failed' }
  }
}

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

export async function syncContacts(): Promise<{ success: boolean; data?: unknown; error?: string }> {
  try {
    const resp = await fetch(`${API_BASE}/admin/sync-contacts`, {
      method: 'POST',
      headers: authHeaders(),
    })
    return await parseJson(resp)
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Sync failed' }
  }
}

// ---------------------------------------------------------------------------
// Helpers (UI)
// ---------------------------------------------------------------------------

export function generateLink(url: string, filename: string, format: 'markdown' | 'url' | 'html'): string {
  switch (format) {
    case 'markdown': return `![${filename}](${url})`
    case 'html':     return `<img src="${url}" alt="${filename}">`
    default:         return url
  }
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

export function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('zh-CN', { year: 'numeric', month: 'short', day: 'numeric' })
}

export function getFileIcon(_filename: string, type: FileItem['type']): string {
  const map: Record<FileItem['type'], string> = { image: 'image', document: 'file-text', other: 'file' }
  return map[type] || 'file'
}
